import type { FC, ReactNode } from 'react'

import { ArrowUp } from 'lucide-react'
import { useLenis } from '@/components/motion/smooth-scroll'
import { Button } from '@/components/generals/button'
import { Marquee } from '@/components/generals/marquee'
import { CortexMark } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { RollText } from '@/components/generals/roll-text'
import { TransitionLink } from '@/components/generals/transition-link'

import { NAV_LINKS, PRIMARY_CTA, siteConfig } from '@/constants/site'
import { SOLUTIONS } from '@/constants/solutions'
import { SERVICES } from '@/constants/services'

interface FooterColumnProps {
    title: string
    links: { label: string, href: string }[]
}

const FooterColumn: FC<FooterColumnProps> = ({ title, links }): ReactNode => {
    return (
        <div className='flex flex-col gap-5'>
            <p className='type-label text-muted-foreground'>
                {title}
            </p>

            <ul className='flex flex-col gap-2.5'>
                {links.map((link) => (
                    <li key={link.href}>
                        <TransitionLink
                            href={link.href}
                            className='group inline-flex text-[0.9375rem] text-soft transition-colors duration-300 hover:text-foreground'
                        >
                            <RollText>{link.label}</RollText>
                        </TransitionLink>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const Footer: FC = (): ReactNode => {
    const lenis = useLenis()

    const backToTop = (): void => {
        if (lenis) lenis.scrollTo(0, { duration: 1.6 })
        else window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <footer className='relative z-[2] overflow-hidden border-t border-line bg-background'>
            <Container className='grid gap-10 pt-20 pb-14 md:pt-28 lg:grid-cols-12 lg:gap-6'>
                <p data-split className='type-statement max-w-[22ch] text-foreground lg:col-span-8'>
                    The quiet systems behind ambitious companies: agents that act, workflows that never stall and data that finally talks to itself.
                </p>

                <div data-reveal className='flex flex-col items-start justify-end gap-6 lg:col-span-4'>
                    <p className='max-w-[34ch] text-soft'>
                        Have a process that should run itself? Tell us about it and we will map the fastest path to automation.
                    </p>

                    <Button href={PRIMARY_CTA.href}>
                        {PRIMARY_CTA.label}
                    </Button>
                </div>
            </Container>

            <TransitionLink
                href={PRIMARY_CTA.href}
                aria-label={PRIMARY_CTA.label}
                className='group block border-y border-line py-6 transition-colors duration-500 hover:bg-white/[0.02] md:py-9'
            >
                <Marquee duration={32}>
                    <span className='type-h1 px-6 whitespace-nowrap text-foreground transition-colors duration-500 group-hover:text-ember md:px-10'>
                        Book a consultation
                    </span>

                    <CortexMark className='size-8 text-ember transition-transform duration-700 ease-expo group-hover:rotate-180 md:size-12' />
                </Marquee>
            </TransitionLink>

            <Container className='grid grid-cols-2 gap-x-6 gap-y-12 py-16 md:grid-cols-4 md:py-20'>
                <FooterColumn
                    title='Navigation'
                    links={[{ label: 'Home', href: '/' }, ...NAV_LINKS]}
                />

                <FooterColumn
                    title='Solutions'
                    links={SOLUTIONS.slice(0, 6).map((solution) => ({
                        label: solution.name,
                        href: `/solutions#${solution.slug}`
                    }))}
                />

                <FooterColumn
                    title='Services'
                    links={SERVICES.map((service) => ({
                        label: service.name,
                        href: `/services#${service.slug}`
                    }))}
                />

                <div className='flex flex-col gap-5'>
                    <p className='type-label text-muted-foreground'>
                        Contact
                    </p>

                    <div className='flex flex-col gap-4 text-[0.9375rem]'>
                        <a
                            href={`mailto:${siteConfig.email}`}
                            className='text-foreground underline decoration-line-strong underline-offset-4 transition-colors hover:decoration-ember'
                        >
                            {siteConfig.email}
                        </a>

                        <p className='text-soft'>
                            Remote-first, working with teams across Europe and North America.
                        </p>

                        <p className='text-soft'>
                            We reply within one business day.
                        </p>
                    </div>
                </div>
            </Container>

            <Container>
                <div className='flex items-end gap-[2.5vw] border-t border-line pt-8 text-foreground md:pt-10'>
                    <CortexMark className='mb-[1.2vw] size-[17vw] max-h-[15rem] max-w-[15rem]' />

                    <span data-split className='type-giant block'>
                        Cortex
                    </span>
                </div>

                <div className='flex flex-col gap-4 border-t border-line py-6 mt-8 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between'>
                    <p>
                        © 2026 Cortex. All rights reserved.
                    </p>

                    <p className='type-label'>
                        AI automation and intelligent systems
                    </p>

                    <button
                        type='button'
                        onClick={backToTop}
                        className='group inline-flex items-center gap-2 self-start text-soft transition-colors hover:text-foreground md:self-auto'
                    >
                        <RollText>Back to top</RollText>
                        <ArrowUp className='size-4 transition-transform duration-500 ease-expo group-hover:-translate-y-0.5' />
                    </button>
                </div>
            </Container>
        </footer>
    )
}
